import type { OnInit } from '@angular/core';
import { Component, inject, Input } from '@angular/core';
import { shouldShowPricesIncludingVAT } from '@customer/entity';
import type {
  ConsumptionValuesVM,
  CostPerUnitVM,
} from '@innogy/become-a-customer/shared/interfaces';
import { Offer } from '@innogy/become-a-customer/shared/interfaces';
import { InnogyComponentRendering, LinkFieldValue } from '@core/jss-models';
import { getFieldValue } from '@core/jss-utils';
import { NgbActiveModal, NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngrx/store';
import type { ComponentFields } from '@sitecore-jss/sitecore-jss-angular';
import { AsyncPipe, CommonModule, CurrencyPipe } from '@angular/common';
import {
  ModalBodyDirective,
  ModalComponent,
  ModalFooterDirective,
} from '@legacy/common-ui-shared';
import { TrackImpressionDirective } from '@analytics/events';
import { getOfferCostsPerUnit } from '@innogy/become-a-customer/shared';

import { AomContentComponent } from '../aom-content/aom-content.component';
import { AomFooterComponent } from '../aom-footer/aom-footer.component';

@Component({
  selector: 'wl-aom-modal',
  templateUrl: './aom-modal.component.html',
  styleUrls: [
    './aom-modal.component.essent.scss',
    './aom-modal.component.ed.scss',
  ],
  standalone: true,
  imports: [
    CommonModule,
    NgbModule,
    ModalComponent,
    TrackImpressionDirective,
    AomContentComponent,
    AomFooterComponent,
    ModalFooterDirective,
    ModalBodyDirective,
    AsyncPipe,
  ],
  providers: [CurrencyPipe],
})
export class AomModalComponent implements OnInit {
  private readonly store$ = inject(Store);

  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() public offer?: Offer;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() public consumptionValuesVM?: ConsumptionValuesVM;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() public showFooter = true;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() orderPage?: LinkFieldValue;

  /**
   * The rendering of the initiating component (e.g. HorizontalOffer) used for tracking.
   */
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() rendering?: InnogyComponentRendering;

  /**
   * The AOM specific rendering containing the fields needed to render the content of the AOM modal
   */
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() aomContent?: InnogyComponentRendering;

  public costPerUnitVM?: CostPerUnitVM;

  public isVATIncluded$ = this.store$.select(shouldShowPricesIncludingVAT);
  readonly #activeModal = inject(NgbActiveModal);

  ngOnInit(): void {
    this.costPerUnitVM = getOfferCostsPerUnit(this.offer);
  }

  get fields(): ComponentFields | undefined {
    return this.aomContent?.fields;
  }

  get modalTitle() {
    const aomModalTitle = getFieldValue(this.rendering, 'AomModalTitle', '');

    return `${aomModalTitle} ${this.offer?.productTitle}`;
  }

  closeModal() {
    this.#activeModal.close();
  }

  get getFieldValueBBAFeatureFlag() {
    return getFieldValue<boolean>(this.rendering?.fields, 'EnableRemoveBBA');
  }
}
